import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';

import { useUrlParams } from '../../Hooks/useUrlParams';
import { Sorter } from '../../utils/bubbles/sort';
import { SortDropdown, SortItem } from '../SortDropdown';
import { useClipboard } from '../../Hooks/useClipboars';
import OpenIcon from '../assets/OpenIcon.svg';
import CloseIcon from '../assets/CloseIcon.svg';
import { LiveDataPumpTokensContext } from '../LiveDataPumpTokensProvider';
import { LiveDataBonkTokensContext } from '../LiveDataBonkTokensProvider';
import { DeriveBubble } from '../../utils/bubbles/types';
import { isBonkBubble } from '../../utils/bubbles/guards';

import { Bubble } from './Bubble';

import './Bubbles.css';
import { HeaderSettingsContainer } from '../HeaderSettings';
import { VolumeCheck } from '../VolumeCheck';
import { MenuColumn, MenuRow, Options, MenuLabel } from '../Options';

interface BubblesProps<T extends Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken> {
    icon?: string;
    bubbleType: DeriveType<T>;
    bubbleName: string;
    sources: Source[];
    bubbles: DeriveBubble<T>[];
    defaultSoundState: boolean;
    loading: boolean | null;
    sort: Array<SortItem & { ASC: Sorter<T>; DESC: Sorter<T> }>;
    getBondingCurveProgress: (token: T) => number;
    calculateMarketCap: (token: T, solPrice: number) => number;
    onBubbleRemove: (id: number) => void;
}

export function Bubbles<T extends Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken>({
    icon,
    bubbleType,
    bubbleName,
    sources,
    bubbles,
    defaultSoundState,
    loading,
    sort,
    getBondingCurveProgress,
    calculateMarketCap,
    onBubbleRemove,
}: BubblesProps<T>) {
    const collapsedLocalstorageKey = `is${bubbleType}Collapsed`;
    const soundLocalstorageKey = `${bubbleType}SoundEnabled`;

    const { isCopied: isTokenMintCopied, copyToClipboard: copyTokenMint } = useClipboard();
    const { walletAddress } = useUrlParams();
    const navigate = useNavigate();

    const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(defaultSoundState);

    const [displayLinked, setDisplayLinked] = useState<boolean>(true);
    const [displayUnlinked, setDisplayUnlinked] = useState<boolean>(true);
    const [soundLinked] = useState<boolean>(true);
    const [soundUnlinked] = useState<boolean>(true);

    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem(collapsedLocalstorageKey);
        return saved !== null ? saved === 'true' : true;
    });

    const [error] = useState<string | null>(null);

    const createToken = useCallback(
        (token: T) => {
            navigate(`TokenCreate?walletAddress=${walletAddress}`, {
                state: {
                    source: 'bubble',
                    token: {
                        name: token.name,
                        symbol: token.symbol,
                        description: token.description,
                        image: token.image,
                        telegram: token.telegram,
                        twitter: token.twitter,
                        website: token.website,
                    },
                },
            });
        },
        [navigate, walletAddress],
    );

    const [sorterFn, setSorterFn] = useState<Sorter<T> | null>(null);

    if (loading) return <div>Loading bubbles...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className={`bubbles ${bubbleType} ${isCollapsed ? 'expanded' : ''}`}>
            <div className={`feed-source ${isCollapsed ? 'expanded' : ''}`}>
                <div className='name'>
                    <img src={icon} alt='Bubble Icon' />
                    <h3>{bubbleName}</h3>
                </div>
                <HeaderSettingsContainer>
                    <div
                        className='collapse-container'
                        onClick={() => {
                            setIsCollapsed((prev) => {
                                localStorage.setItem(collapsedLocalstorageKey, (!prev).toString());
                                return !prev;
                            });
                        }}
                    >
                        <img src={isCollapsed ? CloseIcon : OpenIcon} alt='Toggle Collapse' />
                    </div>
                    <VolumeCheck
                        enabled={isSoundEnabled}
                        onChecked={() => {
                            const next = !isSoundEnabled;
                            setIsSoundEnabled(next);
                            localStorage.setItem(soundLocalstorageKey, String(next));
                        }}
                    />
                    <Options>
                        <MenuRow>
                            <MenuColumn title='DISPLAY'>
                                <MenuLabel>
                                    <input type='checkbox' checked={displayLinked} onChange={() => setDisplayLinked((prev) => !prev)} />
                                    Linked
                                </MenuLabel>
                                <MenuLabel>
                                    <input type='checkbox' checked={displayUnlinked} onChange={() => setDisplayUnlinked((prev) => !prev)} />
                                    Unlinked
                                </MenuLabel>
                            </MenuColumn>
                            <MenuColumn title='SOUND'>
                                <MenuLabel>
                                    <input type='checkbox' checked={soundLinked} />
                                    Linked
                                </MenuLabel>
                                <MenuLabel>
                                    <input type='checkbox' checked={soundUnlinked} />
                                    Unlinked
                                </MenuLabel>
                            </MenuColumn>
                        </MenuRow>
                        <MenuRow>
                            <SortDropdown
                                label='Sort'
                                items={sort}
                                onSort={(key, order) => {
                                    setSorterFn(() => sort.find((item) => item.key === key)?.[order] ?? null);
                                }}
                            />
                        </MenuRow>
                    </Options>
                </HeaderSettingsContainer>
            </div>
            <section className={`${isCollapsed ? 'expanded' : ''}`}>
                {bubbles
                    .filter((bubble) => {
                        const isLinked = bubble.twitterUsername !== null;
                        if (isLinked && displayLinked) return true;
                        if (!isLinked && displayUnlinked) return true;
                        return false;
                    })
                    .map((bubble) => {
                        const isUnknownProfile = bubble.twitterUsername ? sources.map((source) => source.username).includes(bubble.twitterUsername) : false;

                        return isBonkBubble(bubble) ? (
                            <Bubble
                                key={bubble.id}
                                bubble={{
                                    ...bubble,
                                    letsBonkTokens: sorterFn
                                        ? Array.from(bubble.letsBonkTokens).sort(sorterFn as Sorter<Api.Bonk.DTO.LetsBonkToken>)
                                        : bubble.letsBonkTokens,
                                }}
                                isCollapsed={isCollapsed}
                                isUnknownProfile={isUnknownProfile}
                                copiedMint={isTokenMintCopied}
                                calculateMarketCap={calculateMarketCap as (t: Api.Bonk.DTO.LetsBonkToken) => number}
                                getBondingCurveProgress={getBondingCurveProgress as (t: Api.Bonk.DTO.LetsBonkToken) => number}
                                liveDataCtx={LiveDataBonkTokensContext}
                                onCopy={copyTokenMint}
                                onRemove={onBubbleRemove}
                                onNavigate={createToken as (t: Api.Bonk.DTO.LetsBonkToken) => void}
                            />
                        ) : (
                            <Bubble
                                key={bubble.id}
                                bubble={{
                                    ...bubble,
                                    pumpFunTokens: sorterFn
                                        ? Array.from(bubble.pumpFunTokens).sort(sorterFn as Sorter<Api.Pump.DTO.PumpFunToken>)
                                        : bubble.pumpFunTokens,
                                }}
                                isCollapsed={isCollapsed}
                                isUnknownProfile={isUnknownProfile}
                                copiedMint={isTokenMintCopied}
                                calculateMarketCap={calculateMarketCap as (t: Api.Pump.DTO.PumpFunToken) => number}
                                getBondingCurveProgress={getBondingCurveProgress as (t: Api.Pump.DTO.PumpFunToken) => number}
                                liveDataCtx={LiveDataPumpTokensContext}
                                onCopy={copyTokenMint}
                                onRemove={onBubbleRemove}
                                onNavigate={createToken as (t: Api.Pump.DTO.PumpFunToken) => void}
                            />
                        );
                    })}
            </section>
        </div>
    );
}

export type DeriveType<R extends Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken> = R extends Api.Pump.DTO.PumpFunToken ? 'pumpfun' : 'bonk';
